import { Mail, Phone, Briefcase, Star } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { StatusBadge, ComplaintStatus } from "@/components/shared/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { useEmployeeJobs } from "@/hooks/useEmployees";
import { Employee } from "@/api/employee.api";

interface EmployeeDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Employee | null;
}

export function EmployeeDetailDialog({ open, onOpenChange, employee }: EmployeeDetailDialogProps) {
  const { data, isLoading } = useEmployeeJobs(employee?.id, 1, 10);

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{employee.fullName}</DialogTitle>
          <DialogDescription>{employee.designation ?? "Technician"}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-4 text-sm">
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-data">{employee.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{employee.user.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
            <span>
              {employee.activeJobs} active · {employee.completedJobs} completed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Performance score: {employee.performanceScore.toFixed(1)}</span>
          </div>
        </div>

        <div>
          <h4 className="mb-2 font-display text-sm font-semibold">Assigned jobs</h4>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !data || data.items.length === 0 ? (
            <p className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
              No jobs assigned to this employee yet.
            </p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {data.items.map((job) => (
                <div
                  key={job.id}
                  className="service-tag flex items-center justify-between border border-border bg-card px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium font-data">{job.complaintNumber}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {job.customer.fullName} · {job.productName} ·{" "}
                      {format(new Date(job.createdAt), "dd MMM yyyy")}
                    </p>
                  </div>
                  <StatusBadge status={job.status as ComplaintStatus} />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
